import { Layout } from '../components/Layout';
import { get } from '../api/superAdminClient';
import useSWR from 'swr';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ErrorDisplay } from '../components/ErrorDisplay';

/**
 * 租戶列表回應類型
 */
interface TenantListResponse {
  success: boolean;
  data: {
    tenants: Array<{
      id: string;
      name: string;
      isActive: boolean;
    }>;
    total: number;
  };
}

/**
 * Feature Flags 列表回應類型
 */
interface FeatureFlagListResponse {
  success: boolean;
  data: Array<{
    id: string;
    featureKey: string;
    featureName: string;
    isGloballyEnabled: boolean;
  }>;
}

/**
 * LITE 統計回應類型
 */
interface LiteStatsResponse {
  success: boolean;
  data: {
    totalGroups: number;
    activeGroups: number;
    totalActivities: number;
    totalLiteUsers: number;
    totalParticipantItems: number;
  };
}

/**
 * Dashboard 頁面
 */
export function Dashboard() {
  // 取得租戶統計
  const {
    data: tenantsData,
    error: tenantsError,
    isLoading: tenantsLoading,
  } = useSWR<TenantListResponse>(
    '/api/super-admin/tenants?page=1&limit=1',
    get as any
  );

  // 取得 Feature Flags 統計
  const {
    data: flagsData,
    error: flagsError,
    isLoading: flagsLoading,
  } = useSWR<FeatureFlagListResponse>(
    '/api/super-admin/feature-flags',
    get as any
  );

  // 取得 LITE 統計
  const {
    data: liteData,
    error: liteError,
    isLoading: liteLoading,
  } = useSWR<LiteStatsResponse>('/api/super-admin/lite/stats', get as any);

  // 計算統計數據
  const totalTenants = tenantsData?.data?.total || 0;
  const activeTenants =
    tenantsData?.data?.tenants?.filter(t => t.isActive).length || 0;
  const totalFeatureFlags = flagsData?.data?.length || 0;
  const totalLiteGroups = liteData?.data?.totalGroups ?? 0;
  const totalLiteActivities = liteData?.data?.totalActivities ?? 0;

  const isLoading = tenantsLoading || flagsLoading || liteLoading;
  const hasError = tenantsError || flagsError || liteError;

  return (
    <Layout title="儀表板">
      {isLoading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <LoadingSpinner size="lg" />
        </div>
      ) : hasError ? (
        <ErrorDisplay
          error={tenantsError || flagsError || liteError || '載入失敗'}
          onRetry={() => window.location.reload()}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  總租戶數
                </h3>
                <p className="text-3xl font-bold text-primary">
                  {totalTenants}
                </p>
              </div>
              <div className="text-4xl">🏢</div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  活躍租戶
                </h3>
                <p className="text-3xl font-bold text-green-600">
                  {activeTenants}
                </p>
                {totalTenants > 0 && (
                  <p className="text-sm text-gray-500 mt-1">
                    {Math.round((activeTenants / totalTenants) * 100)}% 活躍率
                  </p>
                )}
              </div>
              <div className="text-4xl">✅</div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  Feature Flags
                </h3>
                <p className="text-3xl font-bold text-blue-600">
                  {totalFeatureFlags}
                </p>
              </div>
              <div className="text-4xl">🚩</div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  LITE 群組數
                </h3>
                <p className="text-3xl font-bold text-indigo-600">
                  {totalLiteGroups}
                </p>
              </div>
              <div className="text-4xl">💬</div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  LITE 活動數
                </h3>
                <p className="text-3xl font-bold text-purple-600">
                  {totalLiteActivities}
                </p>
              </div>
              <div className="text-4xl">📋</div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
